import React from 'react';
import styled from 'astroturf/react';
import { BodyLink } from 'js/design_system/link/BodyLink';

const ProfileIcon = styled.img`
  aspect-ratio: 1 / 1;
  width: 100%;
  border-radius: 50%;
  background: $ds-off-white;
`;

const Link = styled.a<{ small?: boolean }>`
  --size: 40px;
  display: grid;
  height: var(--size);
  place-items: center;
  width: var(--size);

  &:focus-visible {
    outline: none;
  }

  &:focus-visible img {
    outline: initial;
  }

  &.small {
    --size: 24px;
  }
`;

export const Name = styled(BodyLink)`
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;

  -webkit-font-smoothing: antialiased;

  &:not(:hover) {
    text-decoration: none;
  }
`;

const DEFAULT_PHOTO_URL = '/images/s65_no_pic.png';

type Props = {
  /**
   * This is used for the link's `aria-label`,
   * since the only content is an image.
   */
  fullName: string;
  /**
   * Determines whether the avatar should render with
   * a wrapping `a` tag or a wrapping `div`.
   * This is useful if you want to use this component inside
   * of a link, since `a` tags cannot contain other `a` tags.
   */
  profileUrl?: string;
  photoUrl: string;
  small?: boolean;
  style?: React.CSSProperties;
  className?: string;
};

export default function Avatar({
  fullName,
  photoUrl,
  profileUrl,
  small = false,
  style,
  className,
}: Props) {
  const conditionalHref: { href?: string } = {};
  if (profileUrl != null) {
    conditionalHref.href = profileUrl;
  }

  return (
    <Link
      aria-label={`${fullName}'s profile`}
      as={profileUrl != null ? 'a' : 'div'}
      small={small}
      className={className}
      style={style}
      {...conditionalHref}
    >
      <ProfileIcon
        className={'clickable'}
        alt=""
        src={photoUrl}
        onError={({ currentTarget }) => {
          currentTarget.onerror = null;
          currentTarget.src = DEFAULT_PHOTO_URL;
        }}
      />
    </Link>
  );
}
